import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { Button, Group, Icon, Layout, Tab } from "@/comn/components";
import ReactECharts from "echarts-for-react";
import { useAuth, useModal, useTab, useTheme } from "@/comn/hooks";
import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";
import dayjs from "dayjs";

// import axios from "axios";
import { isEmpty } from "lodash";
import { ptlUtils } from "@/tz/go/egaz/osci/ptl/comn";

const NOTICE_PPUP_HIDE_UNTIL_KEY = "noticePpupHideUntil";

/**
 * Waiting for Approval 탭 노출 여부.
 * 오픈 검토 중이라 현재는 노출하지 않는다. 재개 시 이 값만 true 로 되돌리면 된다.
 */
const SHOW_APRV_TAB = false;

/* ------------------------------------------------------------------
 * LPCO 통계/보고서 요약 위젯
 * ------------------------------------------------------------------
 * 서버 API 로 동작한다. 레이아웃만 확인할 때는 DASHBOARD_USE_SAMPLE 을 true 로 두면
 * 서버 호출 없이 샘플 데이터로 그린다.
 * ------------------------------------------------------------------ */
const DASHBOARD_USE_SAMPLE = false;

/** 요약 위젯이 호출하는 API 경로 (OsciMainController) */
const STSTCL_API = {
    kpi: "/api/v1/osci/comn/main/ststcl/kpi",
    byType: "/api/v1/osci/comn/main/ststcl/by-type",
    byOga: "/api/v1/osci/comn/main/ststcl/by-oga",
    byTrade: "/api/v1/osci/comn/main/ststcl/by-trade",
    bllStts: "/api/v1/osci/comn/main/ststcl/bll-stts",
    monthlyPay: "/api/v1/osci/comn/main/ststcl/monthly-pay",
};

/**
 * 월 축 라벨.
 * 서버는 1월부터 조회 시점 월까지만 내려주므로 데이터 길이에 맞춰 잘라 쓴다.
 * 월 이름은 기존 라벨(L_JAN ~ L_DEC)을 그대로 재사용한다.
 */
const MONTH_LABEL_KEY = [
    "L_JAN", "L_FEB", "L_MAR", "L_APR", "L_MAY", "L_JUN",
    "L_JUL", "L_AUG", "L_SEP", "L_OCT", "L_NOV", "L_DEC",
];
const useMonthLabels = (length: number) => {
    const { t } = useTranslation();
    return MONTH_LABEL_KEY.slice(0, Math.min(length, MONTH_LABEL_KEY.length)).map((key) => t(key));
};

const SAMPLE = {
    kpi: {
        lpcoCnt: 12480,
        lpcoRate: 8.4,
        sbmtCnt: 13950,
        sbmtRate: 6.7,
        payAmt: 4820000000, // [2026-10-05] 10억 단위(4.82) 에서 전체 금액 표기로 변경
        payRate: 5.1,
        bllCnt: 1842,
        bllRate: 6.2,
        unpaidCnt: 214,
        unpaidRate: 11.6,
    },
    /* 유형별 월 추이 (라인) */
    byType: [
        { nm: "Wetcargo Batch Certificate", data: [212, 228, 236, 251, 268, 292, 316, 338] },
        { nm: "Clearance Permit", data: [158, 166, 174, 186, 199, 214, 231, 245] },
        { nm: "Export Permit", data: [96, 102, 108, 115, 122, 131, 140, 149] },
    ],
    byOga: [
        { nm: "OGAM0001", cnt: 277 },
        { nm: "OGAZ0001", cnt: 84 },
        { nm: "OGAZ0041", cnt: 44 },
        { nm: "OGAZ0032", cnt: 39 },
        { nm: "OGAZ0010", cnt: 37 },
    ],
    /* 무역 구분 비중 (단일 세그먼트 바) */
    byTrade: [
        { nm: "Import", rate: 52 },
        { nm: "Export", rate: 38 },
        { nm: "Transit", rate: 10 },
    ],
    bllStts: [
        { nm: "Paid", cnt: 304 },
        { nm: "In Progress", cnt: 92 },
        { nm: "Waiting Issue", cnt: 37 },
        { nm: "Waiting Bill", cnt: 32 },
        { nm: "Cancelled", cnt: 7 },
    ],
    monthlyPay: [612, 634, 651, 668, 682, 697, 712, 728],
};

/**
 * 시안 색상 팔레트.
 * 앞의 5색은 시안 원본이며 KPI 카드·Trade·Bills 위젯이 인덱스로 직접 참조하므로 순서를 바꾸지 않는다.
 * By Type 은 기관에 따라 계열이 10개까지 늘어나 색이 겹치므로 뒤에 색을 덧붙였다.
 */
export const CHART_COLOR = [
    "#5b8ff9",
    "#f0913e",
    "#3fbf8f",
    "#e5b93c",
    "#e5645e",
    "#9270ca",
    "#6dc8ec",
    "#ff99c3",
    "#269a99",
    "#8fbf3f",
    "#c17dd6",
    "#7f8fa6",
];

/**
 * 차트 수치 라벨 색상.
 * ECharts 는 캔버스에 직접 그려 CSS 변수를 해석하지 못하므로 색을 직접 넘겨야 한다.
 * getComputedStyle 로 --text 를 읽으면 테마 토글 시 dark 클래스 변경과 리렌더 순서가
 * 보장되지 않아 직전 테마 색이 그대로 남는다. 테마 상태에서 바로 결정한다.
 * 값은 assets/styles/index.css 의 --text 와 맞춘다. (라이트 15 23 42 / 다크 226 232 240)
 */
const CHART_TEXT_LIGHT = "rgb(15, 23, 42)";
const CHART_TEXT_DARK = "rgb(226, 232, 240)";

/** 전년 대비 증감 기호. 증가 ▲ / 감소 ▼ / 변화 없음·비교 불가 — */
const rateMark = (rate: any) => {
    if (rate === undefined || rate === null) return "—";
    const value = Number(rate);
    if (value > 0) return "▲";
    if (value < 0) return "▼";
    return "—";
};

/** 증감 기호 색상. 변화 없음·비교 불가는 색을 주지 않고 흐린 본문색을 쓴다. */
const rateColor = (rate: any) => {
    if (rate === undefined || rate === null || Number(rate) === 0) return undefined;
    return Number(rate) < 0 ? CHART_COLOR[4] : CHART_COLOR[2];
};

const useChartTextColor = () => {
    const { theme } = useTheme();
    return theme.isDark === "true" ? CHART_TEXT_DARK : CHART_TEXT_LIGHT;
};

/** 대시보드 카드 공통 껍데기. extra 는 헤더 우측 보조 문구. */
export const DashboardCard = ({ title, extra, children, bodyClassName = "p-[20px]" }: any) => (
    <div className="flex-grow-[1] w-full bg-card rounded-[5px] overflow-hidden shadow-md flex flex-col border-[1px]">
        <div className="board-head bg-header flex justify-between items-center px-[20px] py-[10px] box-border border-b-[1px] border-card-border">
            <h3 className="text-base">{title}</h3>
            {extra && <span className="text-xs text-darkgray">{extra}</span>}
        </div>
        <div className={classNames("box-border h-full", bodyClassName)}>{children}</div>
    </div>
);

/** 카드 내부 전환 탭. bare 는 카드 헤더에 얹을 때 사용(하단 여백/선 제거). */
export const TabStrip = ({ items, active, onChange, bare = false }: any) => (
    <div
        className={classNames(
            "flex gap-[4px] border-card-border",
            bare ? "border-b-0" : "border-b-[1px] mb-[16px]",
        )}
    >
        {items.map((item: any) => (
            <button
                key={item.key}
                type="button"
                onClick={() => onChange(item.key)}
                className={classNames(
                    "px-[14px] py-[8px] text-sm rounded-t-[4px] -mb-[1px] border-b-[2px]",
                    active === item.key
                        ? "text-primary border-primary font-semibold"
                        : "text-darkgray border-transparent",
                )}
            >
                {item.label}
                {item.count !== undefined && (
                    <span
                        className={classNames(
                            "ml-[6px] px-[6px] py-[1px] rounded-[8px] text-xs",
                            active === item.key ? "bg-primary text-primary-contrast" : "bg-header text-darkgray",
                        )}
                    >
                        {item.count}
                    </span>
                )}
            </button>
        ))}
    </div>
);

/** 숫자 천단위 구분 */
export const toNumber = (value: any) => Number(value ?? 0).toLocaleString();

/**
 * 요약 위젯 데이터 조회 공통 함수.
 * 서버 API 준비 전이거나 호출이 실패하면 샘플 데이터를 유지한다.
 */
const loadStstcl = async (path: string, key: string, setter: (data: any) => void, sample: any) => {
    if (DASHBOARD_USE_SAMPLE) {
        setter(sample);
        return;
    }
    try {
        const base = process.env.REACT_APP_API_OSCI || "";
        const response = await api.get(base + path);
        /* 서버 응답은 { <key>: { content } } 형태다. 값이 없으면 기존 상태를 유지한다. */
        const content = response.data?.[key]?.content;
        if (content !== undefined && content !== null) setter(content);
    } catch (error) {
        console.log(error);
    }
};

/**
 * Scope of the LPCO / Joint Inspection processing-status charts.
 * !== 처리현황 차트 조회 범위. 시스템 관리자는 전 기관을, 그 외에는 소속 기관 단위로 본다.
 *     기관을 알 수 없는 계정(업체 등)은 null 을 돌려 조회 자체를 건너뛴다. 그대로 두면
 *     서버 매퍼의 <if> 조건이 빠져 전 기관 통계가 노출되기 때문이다. ==!
 */
const getPrcssSttsScope = (auth: any) => {
    if (ptlUtils.hasAuth("auth_osci0003", auth)) {
        return { srchOrgCd: null, srchUsrId: null };
    }
    const orgCd = auth.get("orgCd");
    if (comnUtils.isEmpty(orgCd)) return null;
    return { srchOrgCd: orgCd, srchUsrId: null };
};

/**
 * LPCO 발급/징수 현황 KPI 카드 4종.
 */
const TanogaPortalMainKpi = () => {
    const { t } = useTranslation();
    const [kpi, setKpi] = useState<any>(DASHBOARD_USE_SAMPLE ? SAMPLE.kpi : {});

    useEffect(() => {
        loadStstcl(STSTCL_API.kpi, "kpi", setKpi, SAMPLE.kpi);
    }, []);

    const cards = [
        {
            /* Submitted count sits left of the approved count in the same card: "Submitted / Approved"
               !== 제출 건수를 같은 카드의 승인 건수 왼쪽에 둔다: "제출 / 승인" ==! */
            title: t("L_LPCO_SBMT_APRV_YTD"),
            preValue: toNumber(kpi?.sbmtCnt),
            preRate: kpi?.sbmtRate,
            value: toNumber(kpi?.lpcoCnt),
            /* Two numbers on one line: 28px keeps "99,999 / 99,999" inside the narrowest card (~245px)
               !== 숫자 두 개가 한 줄이라 가장 좁은 카드(약 245px)에도 "99,999 / 99,999" 가 들어가도록 28px ==! */
            valueSize: 28,
            color: CHART_COLOR[0],
            kind: "yoy",
            rate: kpi?.lpcoRate,
        },
        {
            title: t("L_TOT_COLT_YTD"),
            /*
             * [2026-10-05] 10억 단위 환산 표기를 전체 금액 표기로 변경한다.
             * 0.03B 처럼 뭉개지거나 500만 미만이 0.00 으로 보이는 문제가 있었다.
             * 금액은 자릿수가 길어 100 억(10,000,000,000) 까지 들어가도록 이 카드만 폰트를 내린다.
             * lg 분기 직후(1280~1400px) 구간에서는 카드 값 영역이 240px 내외까지 좁아지므로
             * 24px 로 둔다. 24px 에서 100 억 표시에 약 215px 가 필요하다. (28px 는 245px)
             * 변경 전: value: `${Number(kpi?.payAmt ?? 0).toFixed(2)}`, suffix: "B TZS", valueSize: 28
             */
            value: comnUtils.getFormattedNumber(kpi?.payAmt ?? 0),
            suffix: "TZS",
            valueSize: 24,
            color: CHART_COLOR[1],
            kind: "yoy",
            rate: kpi?.payRate,
        },
        {
            title: t("L_BLL_ISSU_YTD"),
            value: toNumber(kpi?.bllCnt),
            color: CHART_COLOR[2],
            kind: "yoy",
            rate: kpi?.bllRate,
        },
        {
            title: t("L_UNPAID_BLL"),
            value: toNumber(kpi?.unpaidCnt),
            color: CHART_COLOR[3],
            kind: "unpaid",
            unpaidRate: kpi?.unpaidRate,
        },
    ];

    return (
        <div className="w-full grid grid-cols-2 auto-rows-fr gap-[10px] h-full">
            {cards.map((card) => (
                <div
                    key={card.title}
                    className="bg-card rounded-[5px] shadow-md border-[1px] overflow-hidden flex flex-col"
                >
                    <b className="block h-[3px] w-full shrink-0" style={{ backgroundColor: card.color }}></b>
                    <div className="px-[12px] py-[8px] flex flex-col justify-center gap-[4px] h-full">
                        <span className="text-xl flex items-center gap-[6px]">
                            <i
                                className="inline-block w-[9px] h-[9px] rounded-full shrink-0"
                                style={{ backgroundColor: card.color }}
                            ></i>
                            {card.title}
                        </span>
                        {/* 수치를 크게 두고, 증감·부가 설명은 아래 줄에 둔다. 한 줄에 같이 두면 네 자리 이상 숫자가 잘린다. */}
                        <div className="flex flex-col gap-[2px]">
                            {/* 프로젝트 폰트 스케일이 3xl(20px)까지라 임의 값으로 지정한다. */}
                            {/* [2026-10-05] 자릿수가 긴 금액 카드만 valueSize 로 폰트를 내린다. 기본은 36px. */}
                            <strong
                                className="leading-none font-bold min-w-0 truncate"
                                style={{ fontSize: `${(card as any).valueSize ?? 36}px` }}
                            >
                                {(card as any).preValue !== undefined && (
                                    <>
                                        {(card as any).preValue}
                                        <span className="font-normal text-darkgray mx-[6px]">/</span>
                                    </>
                                )}
                                {card.value}
                                {card.suffix && <span className="text-sm font-normal ml-[3px]">{card.suffix}</span>}
                            </strong>
                            {/*
                              * 증감 표기는 세 가지다.
                              * 증가 ▲ / 감소 ▼ / 변화 없음·비교 불가 —
                              * 전년 실적이 없으면 서버가 rate 를 null 로 내린다. 이때도 — 0.0% 로 표시한다.
                              */}
                            {card.kind === "yoy" && (
                                <span className="text-sm text-darkgray whitespace-nowrap shrink-0">
                                    {/* Submitted rate first, in the same order as the values !== 수치와 같은 순서로 제출 증감률을 먼저 ==! */}
                                    {(card as any).preValue !== undefined && (
                                        <>
                                            <span
                                                className={classNames({ "text-darkgray": !rateColor((card as any).preRate) })}
                                                style={{ color: rateColor((card as any).preRate) }}
                                            >
                                                {rateMark((card as any).preRate)} {Math.abs(Number((card as any).preRate ?? 0)).toFixed(1)}%
                                            </span>
                                            <span className="mx-[4px]">/</span>
                                        </>
                                    )}
                                    <span
                                        className={classNames({ "text-darkgray": !rateColor(card.rate) })}
                                        style={{ color: rateColor(card.rate) }}
                                    >
                                        {/* 전년 실적이 없어 비교가 불가한 경우에도 자리를 비우지 않고 0.0% 로 표시한다. */}
                                        {rateMark(card.rate)} {Math.abs(Number(card.rate ?? 0)).toFixed(1)}%
                                    </span>
                                    <span className="ml-[6px]">{t("L_VS_LAST_YY")}</span>
                                </span>
                            )}
                            {card.kind === "unpaid" && (
                                <span className="text-sm text-darkgray whitespace-nowrap shrink-0">
                                    {t("L_UNPAID_RATE")}
                                    <span className="ml-[6px] text-red">
                                        {Number(card.unpaidRate ?? 0).toFixed(1)}%
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

/**
 * 처리량 통계 — LPCO 유형별 / 기관별 (모든 기관).
 */
const TanogaPortalMainStstclVolume = () => {
    const { t } = useTranslation();
    const auth: any = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const labelColor = useChartTextColor();
    const [byType, setByType] = useState<any[]>(DASHBOARD_USE_SAMPLE ? SAMPLE.byType : []);
    const [byOga, setByOga] = useState<any[]>(DASHBOARD_USE_SAMPLE ? SAMPLE.byOga : []);

    /*
     * OSC 운영자는 기관별 처리량(By OGA)을, 기관 사용자는 서식별 처리량(By Type)을 본다.
     * 두 위젯은 동시에 노출되지 않으므로 해당하는 쪽만 조회한다.
     */
    const isOscAdmin = ptlUtils.hasAuth("auth_osci0003", auth);

    useEffect(() => {
        if (isOscAdmin) {
            loadStstcl(STSTCL_API.byOga, "byOgaList", setByOga, SAMPLE.byOga);
        } else {
            loadStstcl(STSTCL_API.byType, "byTypeList", setByType, SAMPLE.byType);
        }
    }, [isOscAdmin]);

    /* 서식별 계열 중 가장 긴 월 배열 길이에 축 라벨을 맞춘다. */
    const typeMonthCnt = byType.reduce((max: number, row: any) => Math.max(max, row.data?.length ?? 0), 0);
    const typeMonthLabels = useMonthLabels(typeMonthCnt);

    /* 유형별 월 추이 — 라인 */
    const typeOption = {
        color: CHART_COLOR,
        grid: { left: 8, right: 40, top: 16, bottom: 8, containLabel: true },
        tooltip: { trigger: "axis" },
        xAxis: { type: "category", boundaryGap: false, data: typeMonthLabels, axisLabel: { fontSize: 10 } },
        yAxis: { type: "value", axisLabel: { fontSize: 10 } },
        series: byType.map((row) => ({
            name: row.nm,
            type: "line",
            smooth: true,
            symbolSize: 4,
            data: row.data,
            endLabel: { show: true, fontSize: 10, color: labelColor, formatter: (p: any) => p.value },
        })),
    };

    /* 기관별 처리 건수 — 가로 막대. 모든 기관을 개별 막대로 보여준다. (변경 전: 상위 5개 외 Others 로 묶음) */
    const ogaOption = {
        grid: { left: 8, right: 40, top: 10, bottom: 8, containLabel: true },
        /* 축은 좁아 약어를 쓰고, 툴팁에서 기관 전체명을 보여준다. */
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            formatter: (params: any) => {
                const row = byOga[params?.[0]?.dataIndex] ?? {};
                return `${row.orgNm ?? row.nm ?? ""}<br/>${toNumber(params?.[0]?.value)}`;
            },
        },
        xAxis: { type: "value", axisLabel: { fontSize: 10 } },
        yAxis: { type: "category", inverse: true, data: byOga.map((r) => r.nm), axisLabel: { fontSize: 10 } },
        series: [
            {
                type: "bar",
                barWidth: "55%",
                /* 기관마다 색을 달리해 구분한다. Bills by Status 와 같은 방식이다. */
                data: byOga.map((row, idx) => ({
                    value: row.cnt,
                    itemStyle: { color: CHART_COLOR[idx % CHART_COLOR.length], borderRadius: [0, 3, 3, 0] },
                })),
                label: { show: true, position: "right", fontSize: 10, color: labelColor },
            },
        ],
    };

    return (
        <DashboardCard title={t("T_PRCSS_VOL")} extra={isOscAdmin ? t("L_BY_OGA") : t("L_BY_APP_FM")}>
            {/* 계열명은 툴팁에서 확인되므로 별도 범례는 두지 않는다. */}
            <ReactECharts option={isOscAdmin ? ogaOption : typeOption} notMerge={true} style={{ height: 280 }} />
        </DashboardCard>
    );
};

/**
 * 무역 구분 비중 — 단독 카드.
 */
const TanogaPortalMainTrade = () => {
    const { t } = useTranslation();
    const [byTrade, setByTrade] = useState<any[]>(DASHBOARD_USE_SAMPLE ? SAMPLE.byTrade : []);

    useEffect(() => {
        loadStstcl(STSTCL_API.byTrade, "byTradeList", setByTrade, SAMPLE.byTrade);
    }, []);

    return (
        <DashboardCard title={t("T_TRDE_SHARE")} extra={t("L_BY_TRDE_TP")}>
            <div className="flex flex-col gap-[14px]">
                {/* 부분-전체 비중은 단일 세그먼트 바가 파이보다 비교하기 쉽다. */}
                <div className="flex w-full h-[30px] rounded-[4px] overflow-hidden">
                    {byTrade.map((row, idx) => (
                        <div
                            key={row.nm}
                            style={{ width: `${row.rate}%`, backgroundColor: CHART_COLOR[idx] }}
                            title={`${row.nm} ${row.rate}%`}
                        />
                    ))}
                </div>
                <ul className="flex flex-wrap gap-x-[18px] gap-y-[6px]">
                    {byTrade.map((row, idx) => (
                        <li key={row.nm} className="text-xs text-darkgray flex items-center gap-[6px]">
                            <i
                                className="inline-block w-[7px] h-[7px] rounded-full"
                                style={{ backgroundColor: CHART_COLOR[idx] }}
                            ></i>
                            {row.nm}
                            <b className="text-sm">{row.rate}%</b>
                        </li>
                    ))}
                </ul>
            </div>
        </DashboardCard>
    );
};

/**
 * 고지/결제 통계 — 고지서 상태별 분포, 월별 결제 추이.
 */
const TanogaPortalMainBillPayment = () => {
    const { t } = useTranslation();
    const labelColor = useChartTextColor();
    const [bllStts, setBllStts] = useState<any[]>(DASHBOARD_USE_SAMPLE ? SAMPLE.bllStts : []);
    const [monthlyPay, setMonthlyPay] = useState<any[]>(DASHBOARD_USE_SAMPLE ? SAMPLE.monthlyPay : []);
    const [view, setView] = useState("pay");
    const payMonthLabels = useMonthLabels(monthlyPay.length);

    useEffect(() => {
        loadStstcl(STSTCL_API.bllStts, "bllSttsList", setBllStts, SAMPLE.bllStts);
        loadStstcl(STSTCL_API.monthlyPay, "monthlyPayList", setMonthlyPay, SAMPLE.monthlyPay);
    }, []);

    /* 고지서 상태별 — 가로 막대 */
    const bllOption = {
        grid: { left: 8, right: 40, top: 10, bottom: 8, containLabel: true },
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        xAxis: { type: "value", axisLabel: { fontSize: 10 } },
        yAxis: { type: "category", inverse: true, data: bllStts.map((r) => r.nm), axisLabel: { fontSize: 10 } },
        series: [
            {
                type: "bar",
                barWidth: "55%",
                data: bllStts.map((row, idx) => ({
                    value: row.cnt,
                    itemStyle: { color: CHART_COLOR[idx % CHART_COLOR.length], borderRadius: [0, 3, 3, 0] },
                })),
                label: { show: true, position: "right", fontSize: 10, color: labelColor },
            },
        ],
    };

    /* 월별 결제 총액 — 영역 라인 */
    const payOption = {
        grid: { left: 8, right: 40, top: 20, bottom: 8, containLabel: true },
        /*
         * [2026-10-05] 서버가 월별 결제액을 반올림 없이(소수부 포함) 내리도록 바뀌어,
         * 표시만 소수점 없이 끊는다. toNumber 는 소수부를 그대로 노출한다.
         */
        tooltip: { trigger: "axis", valueFormatter: (value: any) => comnUtils.getFormattedNumber(Math.round(Number(value ?? 0)), 0) },
        xAxis: { type: "category", boundaryGap: false, data: payMonthLabels, axisLabel: { fontSize: 10 } },
        yAxis: { type: "value", show: false, scale: true },
        series: [
            {
                type: "line",
                smooth: true,
                showSymbol: false,
                data: monthlyPay,
                lineStyle: { width: 2, color: CHART_COLOR[0] },
                itemStyle: { color: CHART_COLOR[0] },
                areaStyle: { color: "rgba(91, 143, 249, 0.18)" },
                // [2026-10-05] 소수부를 끊어 표시한다.
                endLabel: { show: true, fontSize: 10, color: labelColor, formatter: (p: any) => comnUtils.getFormattedNumber(Math.round(Number(p.value ?? 0)), 0) },
            },
        ],
    };

    return (
        <DashboardCard title={t("T_BLL_PAY")}>
            <TabStrip
                items={[
                    { key: "pay", label: t("L_MTHLY_PAY") },
                    { key: "bll", label: t("L_BLL_BY_STTS") },
                ]}
                active={view}
                onChange={setView}
            />
            {/* 탭 전환 시 이전 계열이 남지 않도록 옵션을 병합하지 않는다. */}
            <ReactECharts
                option={view === "bll" ? bllOption : payOption}
                notMerge={true}
                style={{ height: 240 }}
            />
        </DashboardCard>
    );
};

/**
 * Shown after login when one or more notices are flagged as popup (ppupYn = 'Y')
 * and the current time falls within their popup display period.
 */
const NoticePpupModal = ({
    data,
    bltbrdId,
    onHideForToday,
    navigate,
}: {
    data: any[];
    bltbrdId: string;
    onHideForToday: () => void;
    navigate: any;
}) => {
    const { t } = useTranslation();
    const modal = useModal();
    const tab = useTab({
        defaultSchema: {
            id: "tab_noticePpup",
            schema: data.map((_: any, index: number) => ({ label: index + 1, link: index })),
        },
    });

    return (
        <>
            <Tab {...tab.tab}>
                {data.map((notice: any, index: number) => (
                    <Tab.Panel key={notice.btbdmsId}>
                        <div className="flex flex-col gap-[10px] p-[10px]">
                            <a
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(
                                        `/osci/ptl/btbdms/osci0106002q/${bltbrdId}/${notice.btbdmsId}?menuId=menu_osci0112`,
                                    );
                                }}
                                className="text-primary underline"
                                style={{ cursor: "pointer" }}
                            >
                                <h4 className="text-base font-semibold">{notice.btbdmsTtleNm}</h4>
                            </a>
                            <div
                                className="h-[300px] overflow-auto"
                                dangerouslySetInnerHTML={{ __html: notice.btbdmsCn }}
                            />
                        </div>
                    </Tab.Panel>
                ))}
            </Tab>
            <Layout direction="row">
                <Layout.Left>
                    <Button onClick={onHideForToday}>{t("B_NOTI_HIDE")}</Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="close" onClick={() => modal.closeModal()} />
                </Layout.Right>
            </Layout>
        </>
    );
};

const TanogaPortalMainNotification = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [foundNotification, setNotification] = useState<any[]>([]);

    const getNotificationList = async () => {
        try {
            let base = "",
                path = "";

            base = process.env.REACT_APP_API_OSCI || "";
            path = `/api/v1/ptlo/comn/comn/main/notification`;
            const response = await api.get(base + path);
            const data = response.data.notificationList.content;
            setNotification(data);
        } catch (error) {
            console.log(error);
            // throw error;
        }
    };

    useEffect(() => {
        getNotificationList();
    }, []);

    return (
        <div className={classNames("dashboard-content-min w-full flex gap-[20px] flex-grow-[1] flex-col")}>
            <div className="board-content flex-grow-[1] agency-board w-full bg-card rounded-[5px] overflow-hidden shadow-md flex flex-col justify-between border-[1px]">
                <div className="board-head bg-header flex justify-between items-center px-[20px] py-[10px] box-border border-b-[1px] border-card-border">
                    <p className="flex gap-[10px] items-center">
                        <h3 className="text-base">{t("T_TOT_NTC_LST")}</h3>
                        {/* <span className="text-darkgray">나의 할일과 결재 현황</span> */}
                    </p>
                </div>
                <div className="board-body p-[20px] box-border h-full">
                    {foundNotification &&
                        foundNotification.map((notification, index) => (
                            <div className="noti-head flex flex-col gap-[10px]">
                                <div className="noti-title flex gap-[4px] justify-between items-center flex-wrap">
                                    <a
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(
                                                `/ptlo/ptl/btbdms/ptli1018002q/${notification.bltbrdId}/${notification.btbdmsId}?menuId=menu_ptlo0021`,
                                            );
                                        }}
                                    >
                                        <h4>{notification.btbdmsTtleNm}</h4>
                                    </a>
                                    <div style={{ display: "flex", gap: "20px" }}>
                                        <p style={{ fontWeight: "bold", margin: 0 }}>{notification.selcCnt}</p>
                                        <p style={{ fontWeight: "bold", margin: 0 }}>{notification.frstRegstId}</p>
                                        <span style={{ color: "#888888", whiteSpace: "nowrap" }}>
                                            {notification.frstRgsrDtm}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
};

const TanogaPortalMainInsc = () => {
    const auth: any = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const [totInscCnt, setTotInscCnt] = useState<any[]>([]);
    const [prcdInscCnt, setPrcdInscCnt] = useState<any[]>([]);
    const [doneInscCnt, setDoneInscCnt] = useState<any[]>([]);
    const { t } = useTranslation();

    const getTotInscCnt = async () => {
        try {
            let base = "",
                path = "";

            base = process.env.REACT_APP_API_OSCI || "";
            path = `/api/v1/osci/ptl/insc/tot-insc-cnt`;

            const paramData = getPrcssSttsScope(auth);
            if (paramData === null) return;

            const response = await api.get(base + path, {
                params: comnUtils.toGetParams(paramData),
            });

            const data = response.data.totInscCnt.content;
            let chartData: any = [];
            if (!comnUtils.isEmpty(data)) {
                Object.entries(data).forEach((value) => {
                    if (value[0].indexOf("Cnt") > 0) {
                        chartData.push({ value: value[1] });
                    }
                });
            }

            setTotInscCnt(chartData);
        } catch (error) {
            console.log(error);
            // throw error;
        }
    };

    const getPrcdInscCnt = async () => {
        try {
            let base = "",
                path = "";

            base = process.env.REACT_APP_API_OSCI || "";
            path = `/api/v1/osci/ptl/insc/prcd-insc-cnt`;

            const paramData = getPrcssSttsScope(auth);
            if (paramData === null) return;

            const response = await api.get(base + path, {
                params: comnUtils.toGetParams(paramData),
            });

            const data = response.data.prcdInscCnt.content;
            let chartData: any = [];
            if (!comnUtils.isEmpty(data)) {
                Object.entries(data).forEach((value) => {
                    if (value[0].indexOf("Cnt") > 0) {
                        chartData.push({ value: value[1] });
                    }
                });
            }

            setPrcdInscCnt(chartData);
        } catch (error) {
            console.log(error);
            // throw error;
        }
    };

    const getDoneInscCnt = async () => {
        try {
            let base = "",
                path = "";

            base = process.env.REACT_APP_API_OSCI || "";
            path = `/api/v1/osci/ptl/insc/done-insc-cnt`;

            const paramData = getPrcssSttsScope(auth);
            if (paramData === null) return;

            const response = await api.get(base + path, {
                params: comnUtils.toGetParams(paramData),
            });

            const data = response.data.doneInscCnt.content;
            let chartData: any = [];
            if (!comnUtils.isEmpty(data)) {
                Object.entries(data).forEach((value) => {
                    if (value[0].indexOf("Cnt") > 0) {
                        chartData.push({ value: value[1] });
                    }
                });
            }

            setDoneInscCnt(chartData);
        } catch (error) {
            console.log(error);
            // throw error;
        }
    };

    useEffect(() => {
        getTotInscCnt();
        getPrcdInscCnt();
        getDoneInscCnt();
    }, []);

    return (
        <div className="flex-grow-[1] w-full bg-card rounded-[5px] overflow-hidden shadow-md flex flex-col justify-between border-[1px]">
            <div className="board-head bg-header flex justify-between items-center px-[20px] py-[10px] box-border border-b-[1px] border-card-border">
                <h3 className="text-base">{t("T_JNT_INSC_PRCSS_STTS")}</h3>
            </div>
            <div className="p-[20px] box-border h-full">
                <ul className="grid grid-cols-1 w-full h-full gap-[30px]">
                    <li className="flex flex-col justify-start items-start gap-[10px]">
                        {/* <h4>{t("L_AL_APP_CNT")}</h4> */}
                        <div className="border border-1 border-lightgray w-full h-full">
                            <ReactECharts
                                option={{
                                    tooltip: {
                                        trigger: "axis",
                                        axisPointer: { type: "cross" },
                                    },
                                    xAxis: {
                                        type: "category",
                                        data: [
                                            t("L_JAN"),
                                            t("L_FEB"),
                                            t("L_MAR"),
                                            t("L_APR"),
                                            t("L_MAY"),
                                            t("L_JUN"),
                                            t("L_JUL"),
                                            t("L_AUG"),
                                            t("L_SEP"),
                                            t("L_OCT"),
                                            t("L_NOV"),
                                            t("L_DEC"),
                                        ],
                                        axisLabel: {
                                            fontSize: 10,
                                            interval: 0,
                                            rotate: 30,
                                        },
                                    },
                                    yAxis: {
                                        type: "value",
                                        /* 건수 차트라 눈금을 정수 1 단위로 둔다. 값이 작을 때 0 으로만 보이는 것을 막는다. */
                                        minInterval: 1,
                                    },
                                    series: [
                                        {
                                            type: "bar",
                                            barWidth: "15%",
                                            data: totInscCnt,
                                            name: t("L_TOT_CNT"),
                                        },
                                        {
                                            type: "bar",
                                            barWidth: "15%",
                                            data: prcdInscCnt,
                                            name: t("L_INSC_PRCD_CNT"),
                                            backgroundStyle: {
                                                color: "rgba(180, 180, 180, 0.2)",
                                            },
                                            itemStyle: {
                                                borderRadius: [0, 0, 0, 0],
                                                borderColor: "#f4645e",
                                                color: "#f4645e",
                                            },
                                        },
                                        {
                                            type: "bar",
                                            barWidth: "15%",
                                            data: doneInscCnt,
                                            name: t("L_INSC_DONE_CNT"),
                                        },
                                    ],
                                }}
                            />
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    );
};

const TanogaPortalMainLPCO = () => {
    const auth: any = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const [totAppCnt, setTotAppCnt] = useState<any[]>([]);
    const [prcdAppCnt, setPrcdAppCnt] = useState<any[]>([]);
    const [doneAppCnt, setDoneAppCnt] = useState<any[]>([]);
    const { t } = useTranslation();

    const getTotAppCnt = async () => {
        try {
            let base = "",
                path = "";

            base = process.env.REACT_APP_API_OSCI || "";
            path = `/api/v1/osci/ptl/app/tot-app-cnt`;

            const paramData = getPrcssSttsScope(auth);
            if (paramData === null) return;

            const response = await api.get(base + path, {
                params: comnUtils.toGetParams(paramData),
            });

            const data = response.data.totAppCnt.content;
            let chartData: any = [];
            if (!comnUtils.isEmpty(data)) {
                Object.entries(data).forEach((value) => {
                    if (value[0].indexOf("Cnt") > 0) {
                        chartData.push({ value: value[1] });
                    }
                });
            }

            setTotAppCnt(chartData);
        } catch (error) {
            console.log(error);
            // throw error;
        }
    };

    const getPrcdAppCnt = async () => {
        try {
            let base = "",
                path = "";

            base = process.env.REACT_APP_API_OSCI || "";
            path = `/api/v1/osci/ptl/app/prcd-app-cnt`;

            const paramData = getPrcssSttsScope(auth);
            if (paramData === null) return;

            const response = await api.get(base + path, {
                params: comnUtils.toGetParams(paramData),
            });

            const data = response.data.prcdAppCnt.content;
            let chartData: any = [];
            if (!comnUtils.isEmpty(data)) {
                Object.entries(data).forEach((value) => {
                    if (value[0].indexOf("Cnt") > 0) {
                        chartData.push({ value: value[1] });
                    }
                });
            }

            setPrcdAppCnt(chartData);
        } catch (error) {
            console.log(error);
            // throw error;
        }
    };

    const getDoneAppCnt = async () => {
        try {
            let base = "",
                path = "";

            base = process.env.REACT_APP_API_OSCI || "";
            path = `/api/v1/osci/ptl/app/done-app-cnt`;

            const paramData = getPrcssSttsScope(auth);
            if (paramData === null) return;

            const response = await api.get(base + path, {
                params: comnUtils.toGetParams(paramData),
            });

            const data = response.data.doneAppCnt.content;
            let chartData: any = [];
            if (!comnUtils.isEmpty(data)) {
                Object.entries(data).forEach((value) => {
                    if (value[0].indexOf("Cnt") > 0) {
                        chartData.push({ value: value[1] });
                    }
                });
            }

            setDoneAppCnt(chartData);
        } catch (error) {
            console.log(error);
            // throw error;
        }
    };

    useEffect(() => {
        getTotAppCnt();
        getPrcdAppCnt();
        getDoneAppCnt();
    }, []);

    return (
        // <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-[20px]">
        <div className="flex-grow-[1] w-full bg-card rounded-[5px] overflow-hidden shadow-md flex flex-col justify-between border-[1px]">
            <div className="board-head bg-header flex justify-between items-center px-[20px] py-[10px] box-border border-b-[1px] border-card-border">
                <h3 className="text-base">{t("T_LPCO_PRCSS_STTS")}</h3>
            </div>
            <div className="p-[20px] box-border h-full">
                <ul className="grid grid-cols-1 w-full h-full gap-[30px]">
                    <li className="flex flex-col justify-start items-start gap-[10px]">
                        {/* <h4>{t("L_AL_APP_CNT")}</h4> */}
                        <div className="border border-1 border-lightgray w-full h-full">
                            <ReactECharts
                                option={{
                                    tooltip: {
                                        trigger: "axis",
                                        axisPointer: { type: "cross" },
                                    },
                                    xAxis: {
                                        type: "category",
                                        data: [
                                            t("L_JAN"),
                                            t("L_FEB"),
                                            t("L_MAR"),
                                            t("L_APR"),
                                            t("L_MAY"),
                                            t("L_JUN"),
                                            t("L_JUL"),
                                            t("L_AUG"),
                                            t("L_SEP"),
                                            t("L_OCT"),
                                            t("L_NOV"),
                                            t("L_DEC"),
                                        ],
                                        axisLabel: {
                                            fontSize: 10,
                                            interval: 0,
                                            rotate: 30,
                                        },
                                    },
                                    yAxis: {
                                        type: "value",
                                        /* 건수 차트라 눈금을 정수 1 단위로 둔다. 값이 작을 때 0 으로만 보이는 것을 막는다. */
                                        minInterval: 1,
                                    },
                                    series: [
                                        {
                                            type: "bar",
                                            barWidth: "15%",
                                            data: totAppCnt,
                                            name: t("L_TOT_CNT"),
                                        },
                                        {
                                            type: "bar",
                                            barWidth: "15%",
                                            data: prcdAppCnt,
                                            name: t("L_APP_PRCD_CNT"),
                                            backgroundStyle: {
                                                color: "rgba(180, 180, 180, 0.2)",
                                            },
                                            itemStyle: {
                                                borderRadius: [0, 0, 0, 0],
                                                borderColor: "#f4645e",
                                                color: "#f4645e",
                                            },
                                        },
                                        {
                                            type: "bar",
                                            barWidth: "15%",
                                            data: doneAppCnt,
                                            name: t("L_APP_DONE_CNT"),
                                        },
                                    ],
                                }}
                            />
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        // </div>
    );
};

/**
 * 내 할일 / 결재 대기 / 기관 공지를 한 카드에서 탭으로 전환한다.
 * 데이터는 기존 메인 API 를 그대로 사용한다.
 */
const TanogaPortalMainWorkTabs = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal();
    const auth: any = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const [view, setView] = useState("todo");
    const [todo, setTodo] = useState<any[]>([]);
    const [aprv, setAprv] = useState<any[]>([]);
    const [notice, setNotice] = useState<any[]>([]);

    const noticeBltbrdId = "14270d90-17af-e42d-e063-03001facae2d";

    /* ---- 진입 시 공지 팝업 ---- */
    const isPpupHiddenForToday = () => {
        const hideUntil = window.localStorage.getItem(NOTICE_PPUP_HIDE_UNTIL_KEY);
        if (!hideUntil) return false;
        return dayjs().isBefore(dayjs(hideUntil));
    };

    const handleHidePpupForToday = () => {
        window.localStorage.setItem(NOTICE_PPUP_HIDE_UNTIL_KEY, dayjs().add(1, "day").startOf("day").toISOString());
        modal.closeModal();
    };

    const openNoticePpup = (ppupList: any[]) => {
        if (!ppupList.length || isPpupHiddenForToday()) return;
        modal.openModal({
            content: (
                <NoticePpupModal
                    data={ppupList}
                    bltbrdId={noticeBltbrdId}
                    onHideForToday={handleHidePpupForToday}
                    navigate={navigate}
                />
            ),
            title: t("L_OGA_ORG_NTC"),
            size: "sm",
            height: "500px",
            draggable: true,
            close: false,
        });
    };

    /** 게시 기간 안에 있는 팝업 공지만 추린다. */
    const pickPpupList = (rows: any[]) => {
        const now = dayjs();
        return (rows || []).filter((row: any) => {
            if (row.ppupYn !== "Y") return false;
            if (!row.ppupStrtDtm || !row.ppupEndDtm) return false;
            return now.isAfter(dayjs(row.ppupStrtDtm)) && now.isBefore(dayjs(row.ppupEndDtm));
        });
    };

    const load = async (path: string, pick: (data: any) => any, setter: (rows: any[]) => void) => {
        try {
            const base = process.env.REACT_APP_API_OSCI || "";
            const response = await api.get(base + path);
            setter(pick(response.data) || []);
        } catch (error) {
            console.log(error);
        }
    };

    /*
     * OSC 운영자는 기관 공지만 보고, 기관 사용자는 My Task 와 기관 공지를 본다.
     * 노출하지 않는 목록은 조회하지 않는다.
     */
    const isOscAdmin = ptlUtils.hasAuth("auth_osci0003", auth);

    useEffect(() => {
        if (!isOscAdmin) {
            load("/api/v1/osci/comn/main/todo", (d) => d?.todoList?.content, setTodo);
            if (SHOW_APRV_TAB) {
                load("/api/v1/osci/comn/main/aprv", (d) => d?.aprvList?.content, setAprv);
            }
        }
        load(`/api/v1/osci/comn/main/notice?bltbrdId=${noticeBltbrdId}`, (d) => d?.ogaNtcList?.content, (rows) => {
            setNotice(rows);
            openNoticePpup(pickPpupList(rows));
        });
    }, [isOscAdmin]);

    const moveTodo = (row: any) => {
        const reffId = row.todoReffId?.charAt(0) === "/" ? row.todoReffId : `/${row.todoReffId ?? ""}`;
        const url = `${process.env.REACT_APP_API_FRONT_OSCI}${row.scrnDocUrlAddr}${reffId}`;
        const joiner = reffId.indexOf("?") === -1 ? "?" : "&";
        window.open(`${url}${joiner}menuId=mytodo&ptlTodoPrcdStatCd=${row.ptlTodoPrcdStatCd}`, "_parent");
    };

    const moveAprv = (row: any) => {
        const waitQuery = row.itsfAprvYn === "Y" ? "" : "flag=W&";
        navigate(`/osci/comnmdle/aprv/osci0108005s/${row.aprvId}/${row.usrDmnId}?${waitQuery}menuId=menu_osci0110`);
    };

    const moveNotice = (row: any) => {
        navigate(`/osci/ptl/btbdms/osci0106002q/${noticeBltbrdId}/${row.btbdmsId}?menuId=menu_osci0112`);
    };

    /* 관리자는 기관 공지 하나만, 기관 사용자는 My Task 와 기관 공지를 노출한다. */
    const tabItems = isOscAdmin
        ? [{ key: "notice", label: t("L_OGA_ORG_NTC") }]
        : [
              { key: "todo", label: t("T_MY_TODO"), count: todo.length },
              ...(SHOW_APRV_TAB ? [{ key: "aprv", label: t("L_APRV_WAIT_TAB"), count: aprv.length }] : []),
              { key: "notice", label: t("L_OGA_ORG_NTC") },
          ];

    /* 권한 정보가 늦게 채워지거나 탭이 빠져도 선택 상태가 어긋나지 않도록 보정한다. */
    const activeView = tabItems.some((item) => item.key === view) ? view : tabItems[0].key;

    /** 목록 한 줄 */
    const Row = ({ label, right, onClick }: any) => (
        <li className="w-full flex justify-between items-center gap-[10px] py-[5px] border-b-[1px] border-card-border last:border-b-0">
            <a
                onClick={(e) => {
                    e.preventDefault();
                    onClick();
                }}
                className="text-sm line-clamp-1"
                style={{ cursor: "pointer" }}
            >
                {label}
            </a>
            {right}
        </li>
    );

    return (
        <DashboardCard
            bodyClassName="px-[20px] py-[8px]"
            title={
                <TabStrip bare items={tabItems} active={activeView} onChange={setView} />
            }
        >
            <ul className="w-full flex flex-col max-h-[150px] overflow-auto">
                {activeView === "todo" &&
                    todo.map((row, idx) => (
                        <Row
                            key={idx}
                            label={row.todoBsopDvn}
                            right={<span className="text-xs text-darkgray">{row.frstRgsrDtm}</span>}
                            onClick={() => moveTodo(row)}
                        />
                    ))}
                {activeView === "aprv" &&
                    aprv.map((row) => (
                        <Row
                            key={row.aprvId}
                            label={row.aprvRqstTtleNm}
                            right={<span className="text-xs text-darkgray">{row.aprvRqstDtm}</span>}
                            onClick={() => moveAprv(row)}
                        />
                    ))}
                {activeView === "notice" &&
                    notice.map((row, idx) => (
                        <Row
                            key={idx}
                            label={row.btbdmsTtleNm}
                            right={
                                <span className="text-xs text-darkgray whitespace-nowrap">
                                    {row.frstRegstId} · {dayjs(row.frstRgsrDtm).isValid()
                                        ? dayjs(row.frstRgsrDtm).format("YYYY-MM-DD")
                                        : row.frstRgsrDtm}
                                </span>
                            }
                            onClick={() => moveNotice(row)}
                        />
                    ))}
            </ul>
        </DashboardCard>
    );
};

const TanogaPortalMain = () => {
    return (
        <div className="dashboard-wrapper h-auto lg:min-h-[calc(100vh-145px)] pb-[20px]" style={{ width: "100%" }}>
            <div className="dashboard-wrap flex flex-col lg:flex-row gap-[20px] h-full" style={{ width: "100%" }}>
                <div
                    className="dashboard-content-wrap w-full lg:w-3/4 h-full flex flex-col gap-[20px] justify-start"
                    style={{ width: "100%" }}
                >
                    {/* 1단 : 업무 탭(할일/결재/공지) + KPI 4종 */}
                    {/*
                      * [2026-10-05] 징수액 KPI 가 전체 금액 표기로 바뀌어 자릿수가 길어졌다.
                      * 업무 탭과 KPI 를 반반(1/2)으로 나눠 카드 폭을 확보한다.
                      * 변경 전: lg:grid-cols-3 에서 업무 탭 lg:col-span-2 (2/3) + KPI 1/3
                      */}
                    <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-[10px] items-stretch">
                        <div className="flex">
                            <TanogaPortalMainWorkTabs />
                        </div>
                        <div className="flex">
                            <TanogaPortalMainKpi />
                        </div>
                    </div>
                    {/* 2단 : 기존 처리현황 차트 */}
                    <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-[10px]">
                        <TanogaPortalMainLPCO />
                        <TanogaPortalMainInsc />
                    </div>
                    {/* 3단 : 통계/보고서 요약 */}
                    <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-[10px] items-stretch">
                        <TanogaPortalMainStstclVolume />
                        <TanogaPortalMainTrade />
                        <TanogaPortalMainBillPayment />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TanogaPortalMain;
